// Accès aux données serveur via TanStack Query.
//
// L'ancien frontend tenait ces caches à la main (`apiMetrics`, `apiPillars`,
// `apiAdoption` indexés par projet, plus des drapeaux `*Loading` épars) : un
// cache de requêtes réécrit, sans invalidation ni déduplication. On prend
// celui qui fait le travail.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api, ApiRequestError, putFile } from './client';
import type {
  AdoptionResponse,
  Interview,
  InterviewPatch,
  BootstrapResponse,
  ComputeAdoptionResponse,
  DemoUsage,
  MaintenanceState,
  MeResponse,
  MetricsResponse,
  MetricItemsResponse,
  Audit,
  CopilotAnswer,
  Evidence,
  EvidenceSuggestionResponse,
  Insight,
  Persona,
  Pillar,
  ProductContext,
  ProductContextSave,
  ContextProposal,
  Mesure0Answers,
  Mesure0Baseline,
  Mesure0Invitation,
  Mesure0Question,
  PublicQuestionnaire,
  StudyPlan,
  PlanSynthesis,
  ProjectTeam,
  ProjectRole,
  PublicTeamInvitation,
  Connector,
  ConnectorType,
  ConnectorMetric,
  ProjectTag,
  ProjectTagsResponse,
  LookbackItem,
  LookbackImportResult,
  Phase,
  Study,
  StudyKind,
  PersonaNeed,
  Seniority,
  StudyPatch,
  StudyProtocol,
  Triangulation,
  UserTest,
  NewClient,
  NewProject,
  ProjectDeletionSummary,
  PersonaDeletionSummary,
  NewStudy,
  NewPersona,
  NewInsight,
  InsightDerivation,
  InsightDeepening,
  OpportunityProposal,
  ObjectivesResponse,
  HypothesisDecision,
  HypothesisStatus,
  TestPatch,
  TestQuestion,
  TestDeletionSummary,
  QuestionDraft,
  ScenarioDraft,
  GuideStatus,
  AuditPatch,
  TriangulationRowPatch,
  IngestResult,
  PassageSuggestion,
  Sentiment,
  SourceDetail,
  StudySource,
  SearchHit,
  Deliverable,
  SectionKey,
  InterviewOutput,
  InterviewOutputFormat,
  InterviewTone,
} from './types';

/** Clés de cache centralisées : une invalidation ratée vient toujours d'une
 *  clé recopiée de travers. */
export const keys = {
  me: ['me'] as const,
  maintenance: ['maintenance'] as const,
  demoUsage: ['demo-usage'] as const,
  bootstrap: ['bootstrap'] as const,
  metrics: (projectId: string, days: number) => ['metrics', projectId, days] as const,
  adoption: (projectId: string) => ['adoption', projectId] as const,
};

async function fetchMe(): Promise<MeResponse | null> {
  try {
    return await api.get<MeResponse>('/api/me');
  } catch (error) {
    // 401 = pas de session. C'est un état normal de l'application, pas une
    // panne : on renvoie `null` plutôt que de propager une erreur qui ferait
    // clignoter un écran d'échec avant l'écran de connexion.
    if (error instanceof ApiRequestError && error.isUnauthenticated) return null;
    throw error;
  }
}

/** Session courante. `null` si non connecté — ce n'est PAS une erreur. */
export function useMe(): UseQueryResult<MeResponse | null> {
  return useQuery({
    queryKey: keys.me,
    queryFn: fetchMe,
    // Une session ne change pas d'elle-même pendant la navigation.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** Le mode maintenance est-il actif ? Public, relu chaque minute : la page de
 *  maintenance s'ouvre et se referme d'elle-même. Un 503 `MAINTENANCE` reçu
 *  ailleurs le fait relire aussitôt (voir main.tsx). */
export function useMaintenance(): UseQueryResult<MaintenanceState> {
  return useQuery({
    queryKey: keys.maintenance,
    queryFn: () => api.get<MaintenanceState>('/api/maintenance'),
    refetchInterval: 60 * 1000,
    staleTime: 30 * 1000,
  });
}

/** Mode démo : où en est l'organisation (« 3 études sur 5 »). `{ demo: false }`
 *  hors démo. Relu après chaque action réussie d'un compte de démo (main.tsx). */
export function useDemoUsage(): UseQueryResult<DemoUsage> {
  return useQuery({
    queryKey: keys.demoUsage,
    queryFn: () => api.get<{ usage: DemoUsage }>('/api/demo/usage').then((b) => b.usage),
    staleTime: 60 * 1000,
  });
}

/** Portefeuille : clients, projets, équipe. Déjà borné au périmètre côté serveur. */
export function useBootstrap(enabled: boolean): UseQueryResult<BootstrapResponse> {
  return useQuery({
    queryKey: keys.bootstrap,
    queryFn: () => api.get<BootstrapResponse>('/api/bootstrap'),
    enabled,
    staleTime: 30 * 1000,
  });
}

/**
 * Réinitialise le cache pour un changement d'identité, puis RELIT la session.
 *
 * DEUX PIÈGES, tous deux constatés en vérification navigateur :
 *
 * 1. `removeQueries()` SANS filtre détruit aussi la requête d'identité — et
 *    DÉTACHE l'observateur monté. Le cache se retrouvait alors avec la bonne
 *    identité et ZÉRO observateur (`["me"] → success / objet / obs:0`) : plus
 *    personne n'écoutait, donc aucun rendu, donc l'écran de connexion persistait
 *    malgré une connexion réussie en 200. On épargne donc `me`.
 *
 * 2. Il faut `refetchQueries` (qui met à jour la requête EXISTANTE, celle que
 *    l'écran observe) et non `fetchQuery` (qui en crée une nouvelle).
 *
 * On attend le résultat avant de rendre la main : l'appelant peut alors
 * considérer l'identité à jour.
 */
async function resetIdentity(qc: QueryClient): Promise<void> {
  // Aucune donnée d'un utilisateur précédent ne doit survivre à un changement
  // de session — c'est le b.a.-ba du cloisonnement côté client.
  qc.removeQueries({ predicate: (q) => q.queryKey[0] !== keys.me[0] });
  await qc.refetchQueries({ queryKey: keys.me });
}

interface Credentials {
  email: string;
  password: string;
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (c: Credentials) => api.post<{ ok: true }>('/api/auth/login', c),
    onSuccess: () => resetIdentity(qc),
  });
}

interface Registration extends Credentials {
  name: string;
  organizationName: string;
  /** Acceptation des conditions de la bêta — exigée par le serveur. */
  acceptTerms: boolean;
  /** Inscription depuis /demo (bêta) : compte ouvert tout de suite, projet exemple fourni. */
  demo?: boolean;
}

export function useRegister() {
  const qc = useQueryClient();
  return useMutation({
    // `/api/auth/register` connecte directement : même suite qu'une connexion.
    // `pending` : le compte attend l'approbation de l'exploitant, aucune session n'est ouverte.
    mutationFn: (r: Registration) => api.post<{ ok: true; pending?: boolean }>('/api/auth/register', r),
    onSuccess: () => resetIdentity(qc),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: true }>('/api/auth/logout'),
    // `onSettled` et non `onSuccess` : même si l'appel échoue, on purge le cache
    // local. Laisser des données de recherche affichées après un clic sur
    // « Se déconnecter » serait pire que tout.
    onSettled: () => {
      // L'ORDRE COMPTE. On pose d'ABORD l'absence de session : la racine bascule
      // alors sur l'écran de connexion, ce qui démonte le portefeuille et ses
      // observateurs. Purger d'abord relançait la requête du portefeuille — encore
      // montée — qui recevait un 401 et affichait « Non authentifié · Réessayer »
      // à la place de l'écran de connexion.
      //
      // On ne redemande pas la session au serveur : le cookie vient d'être
      // révoqué, un aller-retour ne dirait rien de plus.
      qc.setQueryData(keys.me, null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== keys.me[0] });
    },
  });
}

// ---------------------------------------------------------------------------
// TABLEAU DE BORD PROJET
// ---------------------------------------------------------------------------

/** Indicateurs et verdict de constructibilité. Le serveur compte la base réelle. */
export function useMetrics(projectId: string, days = 30) {
  return useQuery({
    queryKey: keys.metrics(projectId, days),
    queryFn: () =>
      api.get<MetricsResponse>(
        `/api/metrics?projectId=${encodeURIComponent(projectId)}&days=${days}`,
      ),
    enabled: Boolean(projectId),
    staleTime: 30 * 1000,
  });
}

/** Les éléments comptés par un indicateur. `key` nul : rien n'est ouvert, on ne
 *  demande rien. La clé de cache commence par `metrics` : « Actualiser » et les
 *  invalidations des indicateurs rafraîchissent aussi la liste ouverte. */
export function useMetricItems(projectId: string, key: string | null) {
  return useQuery({
    queryKey: ['metrics', projectId, 'items', key] as const,
    queryFn: () =>
      api.get<MetricItemsResponse>(
        `/api/metrics/items?projectId=${encodeURIComponent(projectId)}&key=${encodeURIComponent(key ?? '')}`,
      ),
    enabled: Boolean(projectId) && key !== null,
    staleTime: 30 * 1000,
  });
}

/** Notes d'adoption PERSISTÉES. Cette route ne calcule rien : elle relit. */
export function useAdoption(projectId: string) {
  return useQuery({
    queryKey: keys.adoption(projectId),
    queryFn: () => api.get<AdoptionResponse>(`/api/adoption?projectId=${encodeURIComponent(projectId)}`),
    enabled: Boolean(projectId),
    staleTime: 30 * 1000,
  });
}

/**
 * Construit (ou recalcule) le tableau d'adoption.
 *
 * Le seuil de matière est revérifié CÔTÉ SERVEUR et n'a aucune échappatoire :
 * un refus revient en 422 `NOT_READY`, avec le détail de ce qui manque. On ne
 * masque donc pas seulement le bouton — on ne peut pas contourner la règle.
 */
export function useComputeAdoption(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<ComputeAdoptionResponse>('/api/adoption/compute', { projectId }),
    onSuccess: (data) => {
      // On écrit directement le résultat plutôt que de re-solliciter le serveur :
      // la réponse du calcul EST l'état à jour.
      qc.setQueryData(keys.adoption(projectId), {
        ok: true,
        pillars: data.pillars,
        global: data.global,
        computedAt: new Date().toISOString(),
        readiness: data.readiness,
      } satisfies AdoptionResponse);
      // Les indicateurs, eux, peuvent avoir bougé : on les redemande.
      void qc.invalidateQueries({ queryKey: ['metrics', projectId] });
    },
  });
}

// ---------------------------------------------------------------------------
// OBJETS DE RECHERCHE
//
// Un seul motif pour tous : le projet est OBLIGATOIRE dans l'URL, le serveur
// le recoupe avec le périmètre de la session, et un projet hors périmètre
// renvoie une liste vide — jamais une erreur qui révélerait son existence.
// ---------------------------------------------------------------------------

function projectList<T>(name: string, field: string, projectId: string) {
  return {
    queryKey: [name, projectId] as const,
    queryFn: async () => {
      const body = await api.get<Record<string, unknown>>(
        `/api/${name}?projectId=${encodeURIComponent(projectId)}`,
      );
      return (body[field] ?? []) as T[];
    },
    enabled: Boolean(projectId),
    staleTime: 30 * 1000,
  };
}

export const useStudies = (projectId: string) =>
  useQuery(projectList<Study>('studies', 'studies', projectId));

export function useStudy(studyId: string | null) {
  return useQuery({
    queryKey: ['study', studyId] as const,
    queryFn: () =>
      api.get<{ study: Study }>(`/api/studies/${encodeURIComponent(studyId!)}`).then((b) => b.study),
    enabled: Boolean(studyId),
    staleTime: 15 * 1000,
  });
}

/** Ce qui a été recueilli pendant l'étude : un verbatim à la fois. */
export function useStudyVerbatims(studyId: string | null) {
  return useQuery({
    queryKey: ['study-verbatims', studyId] as const,
    queryFn: () =>
      api
        .get<{ verbatims: Evidence[] }>(`/api/studies/${encodeURIComponent(studyId!)}/verbatims`)
        .then((b) => b.verbatims ?? []),
    enabled: Boolean(studyId),
    staleTime: 30 * 1000,
  });
}

export const usePersonas = (projectId: string) =>
  useQuery(projectList<Persona>('personas', 'personas', projectId));

export const useInsights = (projectId: string) =>
  useQuery(projectList<Insight>('insights', 'insights', projectId));

export const useTests = (projectId: string) =>
  useQuery(projectList<UserTest>('tests', 'tests', projectId));

export const useAudits = (projectId: string) =>
  useQuery(projectList<Audit>('audits', 'audits', projectId));

export const useTriangulations = (projectId: string) =>
  useQuery(projectList<Triangulation>('triangulations', 'triangulations', projectId));

export const usePillars = (projectId: string) =>
  useQuery(projectList<Pillar>('pillars', 'pillars', projectId));

/**
 * Un test AVEC son guide de questions et ses personas visés.
 *
 * La liste ne porte que le compte de questions : charger tous les guides du
 * projet pour afficher des cartes serait payer une préparation complète à
 * chaque passage sur l'écran.
 */
export function useTest(projectId: string, testId: string | null) {
  return useQuery({
    queryKey: ['test', projectId, testId] as const,
    queryFn: () =>
      api
        .get<{ test: UserTest }>(`/api/tests/${encodeURIComponent(testId!)}`)
        .then((b) => b.test),
    enabled: Boolean(testId),
    staleTime: 15 * 1000,
  });
}

/** Les faits qui étayent un insight : locuteur, document, horodatage. */
export function useEvidence(insightId: string | null) {
  return useQuery({
    queryKey: ['evidence', insightId] as const,
    queryFn: () =>
      api
        .get<{ evidence: Evidence[] }>(`/api/insights/${encodeURIComponent(insightId!)}/evidence`)
        .then((b) => b.evidence ?? []),
    enabled: Boolean(insightId),
    staleTime: 60 * 1000,
  });
}

/**
 * Copilote sourcé.
 *
 * Une MUTATION et non une requête : poser une question est une action, pas la
 * lecture d'un état. On ne veut ni cache ni rejeu automatique — une question
 * n'est posée que lorsque l'utilisateur la pose.
 */
export function useCopilot(projectId: string) {
  return useMutation({
    mutationFn: (question: string) =>
      api.post<CopilotAnswer>('/api/copilot/ask', { projectId, question }),
  });
}

// ---------------------------------------------------------------------------
// ÉCRITURES
//
// Toutes sont gardées CÔTÉ SERVEUR par `requireImporter` (admin d'organisation
// ou Responsable du projet). Le masquage des formulaires dans l'interface est
// un confort ; c'est le serveur qui refuse.
// ---------------------------------------------------------------------------

/** Invalide tout ce qu'une écriture peut avoir déplacé sur ce projet. */
function invalidateProject(qc: QueryClient, projectId: string) {
  // `test` (singulier) = la fiche détaillée d'un test. Sans elle, modifier le
  // cadrage depuis la fiche rafraîchissait la liste mais pas l'écran ouvert.
  for (const name of ['studies', 'personas', 'insights', 'tests', 'test', 'audits', 'triangulations', 'pillars', 'context', 'study-plan', 'versions', 'board', 'objectives', 'decisions']) {
    void qc.invalidateQueries({ queryKey: [name, projectId] });
  }
  // Les indicateurs et le verdict de constructibilité dépendent de ces volumes.
  void qc.invalidateQueries({ queryKey: ['metrics', projectId] });
  void qc.invalidateQueries({ queryKey: ['adoption', projectId] });
  // Les preuves d'un insight sont indexées par INSIGHT, pas par projet
  // (`['evidence', insightId]`) : seule une invalidation par préfixe les
  // atteint. Sans elle, supprimer un test laissait des preuves fantômes dans
  // une liste dépliée, et rattacher une preuve ne rafraîchissait rien.
  void qc.invalidateQueries({ queryKey: ['evidence'] });
  // Même raison pour la fiche d'une étude et son terrain : indexés par ÉTUDE.
  // Sans ces deux lignes, verser une transcription laissait « Terrain
  // recueilli » vide jusqu'au rechargement de la page.
  void qc.invalidateQueries({ queryKey: ['study'] });
  void qc.invalidateQueries({ queryKey: ['study-verbatims'] });
  // Les transcriptions d'une étude et l'écran de découpage (migration 047).
  void qc.invalidateQueries({ queryKey: ['study-sources'] });
  void qc.invalidateQueries({ queryKey: ['source'] });
}

// --- Découper une transcription (migration 047) ------------------------------

export function useStudySources(studyId: string | null) {
  return useQuery({
    queryKey: ['study-sources', studyId] as const,
    queryFn: () =>
      api.get<{ sources: StudySource[] }>(`/api/studies/${encodeURIComponent(studyId!)}/sources`).then((b) => b.sources),
    enabled: Boolean(studyId),
  });
}

export function useSource(sourceId: string) {
  return useQuery({
    queryKey: ['source', sourceId] as const,
    queryFn: () => api.get<SourceDetail>(`/api/sources/${encodeURIComponent(sourceId)}`),
  });
}

/** Retenir un passage d'un tour de parole comme verbatim. */
export function useCutVerbatim(projectId: string, sourceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { segment: number; text: string; sentiment?: Sentiment; gravity?: string; tags?: string[] }) =>
      api.post<{ verbatim: { id: string } }>(`/api/sources/${encodeURIComponent(sourceId)}/verbatims`, body),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

/** Défaire un découpage (refusé par le serveur si le verbatim est déjà une preuve). */
export function useUncutVerbatim(projectId: string, sourceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (verbatimId: string) =>
      api.del<{ ok: true }>(`/api/sources/${encodeURIComponent(sourceId)}/verbatims/${encodeURIComponent(verbatimId)}`),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

/** Suggestions de l'IA : des passages à retenir, rien n'est écrit. */
export function useSuggestPassages(sourceId: string) {
  return useMutation({
    mutationFn: () =>
      api.post<{ suggestions: PassageSuggestion[]; mode: string }>(`/api/sources/${encodeURIComponent(sourceId)}/suggest`, {}),
  });
}

// --- Contexte produit : l'assistant « Mesure 0 » ---------------------------
//
// Lecture ouverte à tout membre du projet ; écriture, analyse d'URL et piliers
// gardés côté serveur (admin d'organisation ou Responsable du projet).

export function useProductContext(projectId: string) {
  return useQuery({
    queryKey: ['context', projectId] as const,
    queryFn: () =>
      api
        .get<{ context: ProductContext }>(`/api/projects/${encodeURIComponent(projectId)}/context`)
        .then((b) => b.context),
    enabled: Boolean(projectId),
    staleTime: 15 * 1000,
  });
}

/** Enregistre UNE OU PLUSIEURS sections. La réponse est le contexte à jour : on
 *  l'écrit directement dans le cache plutôt que de le redemander. */
export function useSaveProductContext(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: ProductContextSave) =>
      api.put<{ context: ProductContext }>(
        `/api/projects/${encodeURIComponent(projectId)}/context`,
        patch,
      ),
    onSuccess: (data) => {
      qc.setQueryData(['context', projectId], data.context);
      // Les recommandations se calculent sur le contexte : une note ou un métier
      // changé les déplace.
      void qc.invalidateQueries({ queryKey: ['study-plan', projectId] });
      // L'état des lieux peut changer la phase courante, que le filtre affiche.
      void qc.invalidateQueries({ queryKey: ['versions', projectId] });
    },
  });
}

/** Lit le site du produit et PROPOSE un contexte. Une mutation : elle déclenche
 *  une requête sortante et un appel au modèle, ce n'est pas une simple lecture. */
export function useAnalyzeProductUrl(projectId: string) {
  return useMutation({
    mutationFn: (url: string) =>
      api.post<{ proposal: ContextProposal; mode: string; sourceUrl: string }>(
        `/api/projects/${encodeURIComponent(projectId)}/context/analyze`,
        { url },
      ),
  });
}

export function useDeletePillar(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ deleted: true }>(`/api/pillars/${encodeURIComponent(id)}`),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

// --- Mesure 0 : questionnaire, lien client, score déclaré ------------------

export function useMesure0Questions() {
  return useQuery({
    queryKey: ['mesure0-questions'] as const,
    queryFn: () => api.get<{ questions: Mesure0Question[] }>('/api/mesure0/questions').then((b) => b.questions),
    staleTime: Infinity,
  });
}

export function useInternalAnswers(projectId: string) {
  return useQuery({
    queryKey: ['mesure0-answers', projectId] as const,
    queryFn: () =>
      api.get<{ answers: Mesure0Answers }>(`/api/projects/${encodeURIComponent(projectId)}/mesure0/answers`)
        .then((b) => b.answers),
    enabled: Boolean(projectId),
  });
}

export function useSaveInternalAnswers(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (answers: Mesure0Answers) =>
      api.put<{ answers: Mesure0Answers }>(`/api/projects/${encodeURIComponent(projectId)}/mesure0/answers`, { answers }),
    onSuccess: (data) => {
      qc.setQueryData(['mesure0-answers', projectId], data.answers);
      void qc.invalidateQueries({ queryKey: ['context', projectId] });
    },
  });
}

export function useInvitations(projectId: string) {
  return useQuery({
    queryKey: ['mesure0-invitations', projectId] as const,
    queryFn: () =>
      api.get<{ invitations: Mesure0Invitation[] }>(`/api/projects/${encodeURIComponent(projectId)}/mesure0/invitations`)
        .then((b) => b.invitations),
    enabled: Boolean(projectId),
    staleTime: 10 * 1000,
  });
}

function invalidateInvitations(qc: QueryClient, projectId: string) {
  void qc.invalidateQueries({ queryKey: ['mesure0-invitations', projectId] });
  void qc.invalidateQueries({ queryKey: ['context', projectId] });
}

/** Le jeton n'est rendu QU'ICI : le serveur n'en garde que l'empreinte. */
export function useCreateInvitation(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { name?: string; email?: string }) =>
      api.post<{ invitation: { id: string; token: string } }>(
        `/api/projects/${encodeURIComponent(projectId)}/mesure0/invitations`, p,
      ).then((b) => b.invitation),
    onSuccess: () => invalidateInvitations(qc, projectId),
  });
}

export function useRotateInvitation(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ invitation: { id: string; token: string } }>(`/api/mesure0/invitations/${encodeURIComponent(id)}/rotate`)
        .then((b) => b.invitation),
    onSuccess: () => invalidateInvitations(qc, projectId),
  });
}

export function useRevokeInvitation(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ revoked: true }>(`/api/mesure0/invitations/${encodeURIComponent(id)}/revoke`),
    onSuccess: () => invalidateInvitations(qc, projectId),
  });
}

/** Calcule le score DÉCLARÉ (côté serveur) et fige un instantané. */
export function useComputeMesure0(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ baseline: Mesure0Baseline }>(`/api/projects/${encodeURIComponent(projectId)}/mesure0/compute`)
        .then((b) => b.baseline),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['context', projectId] });
      void qc.invalidateQueries({ queryKey: ['study-plan', projectId] });
    },
  });
}

// --- Équipe du projet (Options) --------------------------------------------
//
// La gestion est réservée aux admins d'organisation CÔTÉ SERVEUR (403 sinon).
// Changer l'équipe déplace le périmètre : on invalide aussi le portefeuille
// (qui porte `projectMembers`) et l'annuaire.

export function useProjectTeam(projectId: string) {
  return useQuery({
    queryKey: ['team', projectId] as const,
    queryFn: () => api.get<ProjectTeam>(`/api/projects/${encodeURIComponent(projectId)}/team`),
    enabled: Boolean(projectId),
    staleTime: 10 * 1000,
  });
}

function onTeamChange(qc: QueryClient, projectId: string) {
  return (data: ProjectTeam | unknown) => {
    if (data && typeof data === 'object' && 'members' in data) {
      qc.setQueryData(['team', projectId], data);
    } else {
      void qc.invalidateQueries({ queryKey: ['team', projectId] });
    }
    void qc.invalidateQueries({ queryKey: keys.bootstrap });
  };
}

export function useSetMemberRole(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: ProjectRole }) =>
      api.put<ProjectTeam>(`/api/projects/${encodeURIComponent(projectId)}/team/${encodeURIComponent(userId)}`, { role }),
    onSuccess: onTeamChange(qc, projectId),
  });
}

export function useRemoveMember(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.del<ProjectTeam>(`/api/projects/${encodeURIComponent(projectId)}/team/${encodeURIComponent(userId)}`),
    onSuccess: onTeamChange(qc, projectId),
  });
}

/** Le jeton du lien n'est rendu QU'ICI (le serveur n'en garde que l'empreinte). */
export function useInviteMember(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { email: string; name?: string; role: ProjectRole }) =>
      api.post<{ added: boolean; invitation?: { id: string; token: string; email: string; name: string | null } }>(
        `/api/projects/${encodeURIComponent(projectId)}/team/invitations`, p,
      ),
    onSuccess: onTeamChange(qc, projectId),
  });
}

export function useRotateTeamInvitation(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ invitation: { id: string; token: string; email: string; name: string | null } }>(
        `/api/team/invitations/${encodeURIComponent(id)}/rotate`,
      ).then((b) => b.invitation),
    onSuccess: onTeamChange(qc, projectId),
  });
}

export function useRevokeTeamInvitation(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ revoked: true }>(`/api/team/invitations/${encodeURIComponent(id)}/revoke`),
    onSuccess: onTeamChange(qc, projectId),
  });
}

export function usePublicTeamInvitation(token: string) {
  return useQuery({
    queryKey: ['public-team-invitation', token] as const,
    queryFn: () =>
      api.get<{ invitation: PublicTeamInvitation }>(`/api/public/team-invitations/${encodeURIComponent(token)}`)
        .then((b) => b.invitation),
    retry: false,
    staleTime: Infinity,
  });
}

/** Accepter crée le compte ET ouvre la session (cookie posé par le serveur). */
export function useAcceptTeamInvitation(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { name: string; password: string; acceptTerms: boolean }) =>
      api.post<{ projectId: string }>(`/api/public/team-invitations/${encodeURIComponent(token)}/accept`, p),
    onSuccess: () => resetIdentity(qc),
  });
}

// --- Cycle de vie : phase, version, classement ------------------------------

export function useProjectVersions(projectId: string) {
  return useQuery({
    queryKey: ['versions', projectId] as const,
    queryFn: () =>
      api.get<{ phase: Phase; currentVersion: string | null; versions: string[] }>(
        `/api/projects/${encodeURIComponent(projectId)}/versions`,
      ),
    enabled: Boolean(projectId),
    staleTime: 30 * 1000,
  });
}

export function useSetProjectPhase(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (phase: Phase) =>
      api.put<{ phase: Phase }>(`/api/projects/${encodeURIComponent(projectId)}/phase`, { phase }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['versions', projectId] });
      void qc.invalidateQueries({ queryKey: ['context', projectId] });
    },
  });
}

/** Reclasser une étude, un insight ou une triangulation. */
export function useClassify(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { kind: 'study' | 'insight' | 'triangulation'; id: string; phase: Phase | null; version: string | null }) =>
      api.request<{ ok: true }>('/api/classify', { method: 'PATCH', body: p }),
    onSuccess: () => {
      invalidateProject(qc, projectId);
      void qc.invalidateQueries({ queryKey: ['versions', projectId] });
    },
  });
}

// --- Connecteurs de mesure (Options) ----------------------------------------

export function useConnectorCatalog() {
  return useQuery({
    queryKey: ['connector-catalog'] as const,
    queryFn: () => api.get<{ catalog: ConnectorType[] }>('/api/connectors/catalog').then((b) => b.catalog),
    staleTime: Infinity,
  });
}

export function useConnectors(projectId: string) {
  return useQuery({
    queryKey: ['connectors', projectId] as const,
    queryFn: () =>
      api.get<{ connectors: Connector[]; secretsReady: boolean; canManage: boolean }>(
        `/api/projects/${encodeURIComponent(projectId)}/connectors`,
      ),
    enabled: Boolean(projectId),
    staleTime: 10 * 1000,
  });
}

export interface ConnectorInput {
  kind?: string;
  label?: string;
  config?: Record<string, unknown>;
  /** Absent = secrets inchangés (modification). */
  secrets?: Record<string, string>;
  periodDays?: number;
  connectorId?: string;
}

export function useSaveConnector(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: ConnectorInput & { id?: string }) =>
      id
        ? api.request<{ connector: Connector }>(`/api/connectors/${encodeURIComponent(id)}`, { method: 'PATCH', body })
        : api.post<{ connector: Connector }>(`/api/projects/${encodeURIComponent(projectId)}/connectors`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['connectors', projectId] }),
  });
}

export function useTestConnector(projectId: string) {
  return useMutation({
    mutationFn: (body: ConnectorInput) =>
      api.post<{ sample: ConnectorMetric[] }>(`/api/projects/${encodeURIComponent(projectId)}/connectors/test`, body),
  });
}

export function useDeleteConnector(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ deleted: true }>(`/api/connectors/${encodeURIComponent(id)}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['connectors', projectId] }),
  });
}

// --- Taxonomie du projet (Options) -------------------------------------------

// ---------------------------------------------------------------------------
// OBJECTIFS DU PROJET (migration 049)
// ---------------------------------------------------------------------------

export function useObjectives(projectId: string) {
  return useQuery({
    queryKey: ['objectives', projectId] as const,
    queryFn: () => api.get<ObjectivesResponse>(`/api/projects/${encodeURIComponent(projectId)}/objectives`),
    enabled: Boolean(projectId),
    staleTime: 30 * 1000,
  });
}

export interface ObjectiveInput {
  title?: string;
  metric?: string;
  due?: string;
  importance?: 'principal' | 'secondaire';
}

/** Créer, modifier ou supprimer un objectif change aussi les scores : on rafraîchit le projet. */
export function useCreateObjective(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (o: ObjectiveInput) =>
      api.post<{ id: string }>(`/api/projects/${encodeURIComponent(projectId)}/objectives`, o),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

export function useUpdateObjective(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ObjectiveInput }) =>
      api.request<{ id: string }>(`/api/objectives/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch }),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

export function useDeleteObjective(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ deleted: true }>(`/api/objectives/${encodeURIComponent(id)}`),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

// ---------------------------------------------------------------------------
// SUIVI DES HYPOTHÈSES (migration 051)
// ---------------------------------------------------------------------------

/** Le journal des décisions du projet, du plus récent au plus ancien. */
export function useDecisions(projectId: string) {
  return useQuery({
    queryKey: ['decisions', projectId] as const,
    queryFn: async () =>
      (await api.get<{ decisions: HypothesisDecision[] }>(`/api/projects/${encodeURIComponent(projectId)}/decisions`)).decisions,
    enabled: Boolean(projectId),
    staleTime: 30 * 1000,
  });
}

/** Changer le statut d'une hypothèse : écrit une ligne au journal. */
export function useChangeHypothesisStatus(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, note }: { id: string; status: HypothesisStatus; note?: string }) =>
      api.post<{ id: string; status: HypothesisStatus }>(`/api/insights/${encodeURIComponent(id)}/hypothesis-status`, { status, note }),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

export function useProjectTags(projectId: string) {
  return useQuery({
    queryKey: ['tags', projectId] as const,
    queryFn: () => api.get<ProjectTagsResponse>(`/api/projects/${encodeURIComponent(projectId)}/tags`),
    enabled: Boolean(projectId),
    staleTime: 10 * 1000,
  });
}

export interface TagInput {
  label?: string;
  family?: string | null;
  description?: string | null;
}

/** Renommer, fusionner ou retirer un tag réécrit des verbatims : la fiche
 *  d'étude et les preuves qui les affichent doivent se relire. */
function invalidateTags(qc: QueryClient, projectId: string) {
  void qc.invalidateQueries({ queryKey: ['tags', projectId] });
  void qc.invalidateQueries({ queryKey: ['study-verbatims'] });
  void qc.invalidateQueries({ queryKey: ['evidence'] });
}

export function useCreateTag(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TagInput) =>
      api.post<{ tag: ProjectTag; retagged: number }>(`/api/projects/${encodeURIComponent(projectId)}/tags`, body),
    onSuccess: () => invalidateTags(qc, projectId),
  });
}

export function useUpdateTag(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: TagInput & { id: string }) =>
      api.request<{ tag: ProjectTag; retagged: number }>(`/api/tags/${encodeURIComponent(id)}`, { method: 'PATCH', body }),
    onSuccess: () => invalidateTags(qc, projectId),
  });
}

export function useDeleteTag(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ deleted: true }>(`/api/tags/${encodeURIComponent(id)}`),
    onSuccess: () => invalidateTags(qc, projectId),
  });
}

export function useMergeTags(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ targetId, sourceIds, labels }: { targetId: string; sourceIds?: string[]; labels?: string[] }) =>
      api.post<{ tag: ProjectTag; merged: number; retagged: number }>(
        `/api/tags/${encodeURIComponent(targetId)}/merge`,
        { sourceIds: sourceIds ?? [], labels: labels ?? [] },
      ),
    onSuccess: () => invalidateTags(qc, projectId),
  });
}

/** Supprime un ou plusieurs tags de tous les verbatims du projet (les verbatims restent). */
export function useStripTag(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (labels: string | string[]) =>
      api.post<{ removed: number; retagged: number }>(`/api/projects/${encodeURIComponent(projectId)}/tags/strip`, {
        labels: Array.isArray(labels) ? labels : [labels],
      }),
    onSuccess: () => invalidateTags(qc, projectId),
  });
}

// --- Lookback : connexion OAuth, puis séances → entretiens d'une étude ------------

/** Rend l'adresse d'autorisation Lookback ; l'appelant y envoie le navigateur. */
export function useLookbackConnect() {
  return useMutation({
    mutationFn: (connectorId: string) =>
      api.post<{ url: string }>(`/api/connectors/${encodeURIComponent(connectorId)}/oauth/start`),
  });
}

export function useLookbackTools() {
  return useMutation({
    mutationFn: (connectorId: string) =>
      api.get<{ tools: string[]; recognized: Record<'projects' | 'rounds' | 'recordings' | 'transcript', string | null> }>(
        `/api/connectors/${encodeURIComponent(connectorId)}/lookback/tools`,
      ),
  });
}

export function useLookbackBrowse(
  connectorId: string,
  level: 'projects' | 'rounds' | 'recordings',
  parent: { projectId?: string; roundId?: string } = {},
  enabled = true,
) {
  const params = new URLSearchParams({ level });
  if (parent.projectId) params.set('projectId', parent.projectId);
  if (parent.roundId) params.set('roundId', parent.roundId);
  return useQuery({
    queryKey: ['lookback', connectorId, level, parent.projectId ?? null, parent.roundId ?? null] as const,
    queryFn: () =>
      api.get<{ items: LookbackItem[] }>(`/api/connectors/${encodeURIComponent(connectorId)}/lookback/browse?${params}`)
        .then((b) => b.items),
    enabled,
    staleTime: 30 * 1000,
    retry: false,
  });
}

/** Une séance par appel : chaque séance passe par l'extraction IA, et l'on
 *  veut une progression lisible plutôt qu'une longue requête muette. */
export function useLookbackImport(projectId: string, connectorId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { studyId: string; recordings: LookbackItem[]; consent: boolean }) =>
      api.post<{ imported: number; results: LookbackImportResult[] }>(
        `/api/connectors/${encodeURIComponent(connectorId)}/lookback/import`, body,
      ),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['connectors', projectId] });
      void qc.invalidateQueries({ queryKey: ['lookback', connectorId, 'recordings'] });
      invalidateInterviews(qc, projectId);
      invalidateProject(qc, projectId);
    },
  });
}

/** L'import écrit dans la triangulation et fait bouger indicateurs et plan. */
export function useSyncConnector(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ imported: number; metrics: ConnectorMetric[]; triangulationId: string }>(
        `/api/connectors/${encodeURIComponent(id)}/sync`,
      ),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['connectors', projectId] });
      invalidateProject(qc, projectId);
    },
  });
}

// --- Plan d'études ---------------------------------------------------------

/** Recommandations recalculées par le serveur à chaque lecture : elles suivent
 *  l'état réel du projet, rien n'est stocké qui puisse se désynchroniser. */
export function useStudyPlan(projectId: string) {
  return useQuery({
    queryKey: ['study-plan', projectId] as const,
    queryFn: () =>
      api.get<StudyPlan>(`/api/projects/${encodeURIComponent(projectId)}/study-plan`),
    enabled: Boolean(projectId),
    staleTime: 15 * 1000,
  });
}

export interface RecommendationPatch {
  title?: string;
  objective?: string;
  target?: string;
  type?: string;
  priority?: 'haute' | 'moyenne' | 'basse';
}

/** Modifier, écarter ou restaurer une recommandation. Le serveur renvoie le
 *  plan recalculé : il devient directement l'état du cache. */
export function useAdjustRecommendation(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, action, patch }: { key: string; action: 'edit' | 'dismiss' | 'restore'; patch?: RecommendationPatch }) => {
      const base = `/api/projects/${encodeURIComponent(projectId)}/study-plan/${encodeURIComponent(key)}`;
      return action === 'edit'
        ? api.request<StudyPlan>(base, { method: 'PATCH', body: patch ?? {} })
        : api.post<StudyPlan>(`${base}/${action}`);
    },
    onSuccess: (plan) => qc.setQueryData(['study-plan', projectId], plan),
  });
}

/** Synthèse rédigée par l'IA. Une mutation : c'est un appel au modèle, on ne le
 *  déclenche que lorsqu'on le demande. */
export function useStudySynthesis(projectId: string) {
  return useMutation({
    mutationFn: () =>
      api.post<{ synthesis: PlanSynthesis; mode: string; generatedAt: string }>(
        `/api/projects/${encodeURIComponent(projectId)}/study-plan/synthesis`,
      ),
  });
}

// Accès PUBLIC du répondant : pas de session, c'est le jeton qui autorise.
export function usePublicQuestionnaire(token: string) {
  return useQuery({
    queryKey: ['public-mesure0', token] as const,
    queryFn: () => api.get<PublicQuestionnaire>(`/api/public/mesure0/${encodeURIComponent(token)}`),
    retry: false,
    staleTime: Infinity,
  });
}

/** Le répondant demande un accès au référentiel : rien n'est ouvert, un
 *  administrateur décidera (Options › Équipe). */
export function useRequestAccess(token: string) {
  return useMutation({
    mutationFn: (p: { email: string; name?: string }) =>
      api.post<{ requested: true }>(`/api/public/mesure0/${encodeURIComponent(token)}/access-request`, p),
  });
}

export function useAcceptAccessRequest(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, role }: { requestId: string; role: ProjectRole }) =>
      api.post<{ added: boolean; invitation?: { id: string; token: string; email: string; name: string | null } }>(
        `/api/projects/${encodeURIComponent(projectId)}/team/requests/${encodeURIComponent(requestId)}/accept`, { role },
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['team', projectId] }),
  });
}

export function useDeclineAccessRequest(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) =>
      api.post<{ declined: true }>(`/api/projects/${encodeURIComponent(projectId)}/team/requests/${encodeURIComponent(requestId)}/decline`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['team', projectId] }),
  });
}

export function useSubmitPublicQuestionnaire(token: string) {
  return useMutation({
    mutationFn: (p: { answers: Mesure0Answers; respondentName: string }) =>
      api.put<{ saved: true }>(`/api/public/mesure0/${encodeURIComponent(token)}`, p),
  });
}

// --- Portefeuille : création de clients et de projets ----------------------
//
// Réservées aux ADMINS d'organisation côté serveur (403 sinon). C'est le chemin
// qui sort une organisation neuve de son portefeuille vide. On invalide
// `bootstrap` : c'est lui qui porte clients et projets.

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (c: NewClient) => api.post<{ client: { id: string } }>('/api/clients', c),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.bootstrap }),
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: NewProject) => api.post<{ project: { id: string } }>('/api/projects', p),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.bootstrap }),
  });
}

/**
 * Suppression d'un projet, PREMIER temps : l'inventaire de ce qui disparaîtra.
 *
 * Chargé seulement quand la confirmation est ouverte (`enabled`) : on ne compte
 * pas les objets de tous les projets du portefeuille pour un geste que personne
 * n'a encore demandé.
 */
export function useProjectDeletionSummary(projectId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['project-deletion', projectId] as const,
    queryFn: () =>
      api
        .get<{ summary: ProjectDeletionSummary }>(
          `/api/projects/${encodeURIComponent(projectId)}/deletion`,
        )
        .then((b) => b.summary),
    enabled: enabled && Boolean(projectId),
    // Un inventaire périmé donnerait à confirmer une destruction sur des
    // chiffres faux : on le relit à chaque ouverture.
    staleTime: 0,
  });
}

/** SECOND temps : le nom retapé part au serveur, qui le revérifie et répond
 *  409 `CONFIRM_MISMATCH` s'il ne correspond pas — sans rien supprimer. */
export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, confirm }: { projectId: string; confirm: string }) =>
      api.del<{ deleted: true; name: string }>(`/api/projects/${encodeURIComponent(projectId)}`, {
        confirm,
      }),
    onSuccess: (_data, variables) => {
      // Tout ce qui portait ce projet est devenu faux : le portefeuille comme
      // les caches par projet.
      void qc.invalidateQueries({ queryKey: keys.bootstrap });
      qc.removeQueries({ predicate: (q) => q.queryKey.includes(variables.projectId) });
    },
  });
}

export function useRenameProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, nom }: { projectId: string; nom: string }) =>
      api.request<{ project: { id: string; name: string } }>(`/api/projects/${encodeURIComponent(projectId)}`, {
        method: 'PATCH', body: { nom },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.bootstrap }),
  });
}

/** Duplique le CADRE du projet (contexte, fonctionnalités, métiers, piliers,
 *  équipe) — jamais les données de recherche. Le nouveau projet entre dans le
 *  périmètre : on relit donc aussi la session. */
export function useDuplicateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, nom }: { projectId: string; nom?: string }) =>
      api.post<{ project: { id: string; name: string; copied: Record<string, number> } }>(
        `/api/projects/${encodeURIComponent(projectId)}/duplicate`, nom ? { nom } : {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.bootstrap });
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function useCreateStudy(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (s: NewStudy) =>
      api.post<{ study: { id: string } }>('/api/studies', { ...s, projectId }),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

export function useUpdateStudy(projectId: string, studyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: StudyPatch) =>
      api.put<{ study: Study }>(`/api/studies/${encodeURIComponent(studyId)}`, patch).then((b) => b.study),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

/** Protocole proposé pour un type d'étude — par l'IA si elle est configurée,
 *  sinon un modèle de départ adapté au produit. Rien n'est enregistré. */
export function useSuggestProtocol(projectId: string) {
  return useMutation({
    mutationFn: (input: { kind: StudyKind; source: 'modele' | 'auto'; study: Partial<Pick<Study, 'objective' | 'method' | 'target' | 'context'>> }) =>
      api.post<{ protocol: StudyProtocol | null; mode: 'ia' | 'modele' }>(
        `/api/projects/${encodeURIComponent(projectId)}/study-protocol`, input),
  });
}

/** Besoins et irritants PROPOSÉS pour un métier (moteur repris de Filon).
 *  Rien n'est enregistré : la proposition remplit le formulaire. */
export function useProposePersonaNeeds(projectId: string) {
  return useMutation({
    mutationFn: (input: { role: string; seniority: Seniority | null }) =>
      api.post<{ items: PersonaNeed[]; mode: 'ia' | 'fake' }>(
        `/api/projects/${encodeURIComponent(projectId)}/persona-needs`, input),
  });
}

export function useCreatePersona(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: NewPersona) =>
      api.post<{ persona: { id: string } }>('/api/personas', { ...p, projectId }),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

export function useCreateInsight(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (i: NewInsight) =>
      api.post<{ insight: { id: string } }>('/api/insights', { ...i, projectId }),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

/**
 * Dérivation d'insights depuis une étude.
 *
 * Une MUTATION : c'est un appel au modèle, coûteux, que l'on ne déclenche que
 * sur demande. Rien n'est enregistré — les propositions s'acceptent ensuite une
 * à une par `useCreateInsight`.
 */
/** Creuser un insight : l'IA propose un insight plus profond. Rien n'est enregistré. */
export function useDeepenInsight(_projectId: string) {
  return useMutation({
    mutationFn: (insightId: string) =>
      api.post<InsightDeepening>(`/api/insights/${encodeURIComponent(insightId)}/deepen`, {}),
  });
}

/** L'IA propose hypothèse, cible et métrique d'un insight. Rien n'est enregistré. */
export function useProposeOpportunity() {
  return useMutation({
    mutationFn: (insightId: string) =>
      api.post<OpportunityProposal>(`/api/insights/${encodeURIComponent(insightId)}/opportunity`, {}),
  });
}

export function useDeriveInsights(studyId: string) {
  return useMutation({
    mutationFn: () =>
      api.post<InsightDerivation>(`/api/studies/${encodeURIComponent(studyId)}/derive-insights`, {}),
  });
}

/** Création d'un test. Le même corps que le PUT (`TestPatch`) sert au POST : le
 *  serveur ignore les clés absentes, on n'a donc pas deux contrats à tenir. */
export function useCreateTest(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (t: TestPatch) =>
      api.post<{ test: { id: string } }>('/api/tests', { ...t, projectId }),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

export function useCreateAudit(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: AuditPatch) =>
      api.post<{ audit: { id: string } }>('/api/audits', { ...a, projectId }),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

/** Création d'un croisement de triangulation. Les mesures s'ajoutent ensuite
 *  une à une (route `.../rows`), comme dans l'écran. */
export function useCreateTriangulation(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (t: { objective?: string; context?: string }) =>
      api.post<{ triangulation: { id: string } }>('/api/triangulations', { ...t, projectId }),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

/** Ingestion d'une transcription : c'est ce qui produit les verbatims. */
export function useIngestTranscript(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { text: string; filename?: string; kind?: string; studyId?: string | null; mode?: 'manual' | 'auto' }) =>
      api.post<IngestResult>('/api/ingest/transcript', {
        projectId,
        text: input.text,
        filename: input.filename ?? '',
        kind: input.kind ?? 'paste',
        studyId: input.studyId ?? null,
        mode: input.mode ?? 'auto',
      }),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

/**
 * ASSISTANT DE PREUVES — détecte les verbatims proches d'un titre d'insight.
 *
 * Classement HYBRIDE côté serveur (lexical + sémantique) : contrairement à
 * `useSearch`, un verbatim peut remonter sans partager un seul mot avec le
 * titre. C'est tout l'intérêt — on cherche une idée, pas une chaîne.
 */
export function useEvidenceSuggestions(projectId: string, query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: ['evidence-suggest', projectId, q] as const,
    queryFn: () =>
      api.post<EvidenceSuggestionResponse>('/api/insights/suggest', { projectId, query: q }),
    // Le serveur ignore déjà les requêtes trop courtes ; on lui épargne
    // l'aller-retour.
    enabled: Boolean(projectId) && q.length >= 4,
    // La détection est coûteuse (récupération + embedding) : on garde le
    // résultat d'un titre donné, la frappe revient souvent au même texte.
    staleTime: 60 * 1000,
  });
}

/** Recherche cloisonnée — sert à choisir les preuves d'un insight. */
export function useSearch(projectId: string, query: string, types?: string[]) {
  const q = query.trim();
  return useQuery({
    queryKey: ['search', projectId, q, types?.join(',') ?? ''] as const,
    queryFn: () => {
      const params = new URLSearchParams({ projectId, q });
      if (types) params.set('types', types.join(','));
      return api
        .get<{ results: SearchHit[] }>(`/api/search?${params.toString()}`)
        .then((b) => b.results ?? []);
    },
    enabled: Boolean(projectId),
    staleTime: 15 * 1000,
  });
}

// --- Édition (branche les routes PUT gardées côté serveur) ------------------

export function useUpdatePersona(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<NewPersona> }) =>
      api.put<{ persona: { id: string } }>(`/api/personas/${encodeURIComponent(id)}`, patch),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

/** Inventaire avant suppression d'un persona — relu à chaque ouverture, comme
 *  celui d'un projet : confirmer sur des chiffres périmés n'est pas confirmer. */
export function usePersonaDeletionSummary(personaId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['persona-deletion', personaId] as const,
    queryFn: () =>
      api
        .get<{ summary: PersonaDeletionSummary }>(
          `/api/personas/${encodeURIComponent(personaId)}/deletion`,
        )
        .then((b) => b.summary),
    enabled: enabled && Boolean(personaId),
    staleTime: 0,
  });
}

export function useDeletePersona(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (personaId: string) =>
      api.del<{ deleted: true }>(`/api/personas/${encodeURIComponent(personaId)}`),
    // Les verbatims survivent mais perdent leur attribution : leurs listes et
    // les indicateurs qui en dépendent sont donc à relire, pas seulement la
    // liste des personas.
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

/** Un insight se MODIFIE (titre, sévérité, pilier…), mais ses preuves se gèrent
 *  à part (route dédiée) : on ne les touche pas ici. */
export function useUpdateInsight(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api.put<{ insight: { id: string } }>(`/api/insights/${encodeURIComponent(id)}`, patch),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

/** Édition d'un test. Le rattachement d'une fonctionnalité à un pilier passe
 *  par ici : c'est ce qui la fait peser sur la note du pilier. */
export function useUpdateTest(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TestPatch }) =>
      api.put<{ test: { id: string } }>(`/api/tests/${encodeURIComponent(id)}`, patch),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

// --- Suppression d'un test : voir, puis supprimer --------------------------

/** PREMIER temps : l'inventaire de ce qui disparaîtrait. Chargé seulement quand
 *  le panneau s'ouvre — on ne compte pas les enfants de tous les tests du projet
 *  pour un geste que personne n'a demandé. */
export function useTestDeletionSummary(projectId: string, testId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['test-deletion', projectId, testId] as const,
    queryFn: () =>
      api
        .get<{ summary: TestDeletionSummary }>(`/api/tests/${encodeURIComponent(testId)}/deletion`)
        .then((b) => b.summary),
    enabled: enabled && Boolean(testId),
    // Un inventaire périmé ferait confirmer une destruction sur des chiffres
    // faux : on le relit à chaque ouverture.
    staleTime: 0,
  });
}

export function useDeleteTest(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ testId }: { testId: string }) =>
      api.del<{ deleted: true }>(`/api/tests/${encodeURIComponent(testId)}`),
    onSuccess: (_data, variables) => {
      invalidateProject(qc, projectId);
      // La fiche du test supprimé doit DISPARAÎTRE du cache, pas se recharger :
      // une invalidation relancerait la requête et afficherait une erreur 404
      // là où l'écran est en train de partir.
      qc.removeQueries({ queryKey: ['test', projectId, variables.testId] });
    },
  });
}

/** Rattacher des preuves à un insight EXISTANT — verbatims, fonctionnalités
 *  mesurées, ou les deux. */
export function useAddInsightEvidence(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      insightId,
      verbatimIds,
      testFeatureIds,
    }: {
      insightId: string;
      verbatimIds?: string[];
      testFeatureIds?: string[];
    }) =>
      api.post<{ linked: number; skipped: number }>(
        `/api/insights/${encodeURIComponent(insightId)}/evidence`,
        { verbatimIds, testFeatureIds },
      ),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

// --- Guide de questions (temps 2 de la préparation d'un test) ---------------

/** Enregistre le guide ENTIER : le serveur supprime puis réinsère, l'appel
 *  porte donc toujours la liste complète. Réponse : `validationReset` dit si la
 *  validation a sauté parce que le guide a changé — l'écran doit le dire. */
export function useSaveQuestions(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ testId, questions }: { testId: string; questions: QuestionDraft[] }) =>
      api.put<{ count: number; validationReset: boolean; questions: TestQuestion[] }>(
        `/api/tests/${encodeURIComponent(testId)}/questions`,
        { questions },
      ),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

/**
 * Enregistre le scénario ENTIER : le serveur supprime puis réinsère, l'appel
 * porte donc toujours la liste complète.
 *
 * La réponse contient le score RECALCULÉ par le serveur. L'interface ne le
 * calcule jamais de son côté : la liste, la fiche et le tableau de bord doivent
 * afficher le même chiffre, donc une seule règle, au même endroit.
 */
export function useSaveScenario(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ testId, steps }: { testId: string; steps: ScenarioDraft[] }) =>
      api.put<{ count: number; result: { score: number | null; observed: number; total: number } }>(
        `/api/tests/${encodeURIComponent(testId)}/scenario`,
        { steps },
      ),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

/** Brouillon de scénario depuis une DICTÉE. N'écrit RIEN : les lignes
 *  renvoyées s'ajoutent au tableau, à relire avant d'enregistrer. `mode` dit
 *  d'où vient le découpage — 'model' (IA) ou 'lines' (mécanique). */
export function useDraftScenario() {
  return useMutation({
    mutationFn: ({ testId, text }: { testId: string; text: string }) =>
      api.post<{ mode: 'model' | 'lines'; steps: ScenarioDraft[] }>(
        `/api/tests/${encodeURIComponent(testId)}/scenario/draft`,
        { text },
      ),
  });
}

/** Import d'un guide existant. N'écrit RIEN côté serveur : renvoie une
 *  proposition que l'utilisateur relit avant d'enregistrer. `mode` dit d'où
 *  vient le découpage — 'model' (mise en forme par l'IA) ou 'lines' (mécanique). */
export function useImportQuestions() {
  return useMutation({
    mutationFn: ({ testId, text, useAi }: { testId: string; text: string; useAi: boolean }) =>
      api.post<{ mode: 'model' | 'lines'; aiAvailable: boolean; questions: QuestionDraft[] }>(
        `/api/tests/${encodeURIComponent(testId)}/questions/import`,
        { text, useAi },
      ),
  });
}

/** Circuit de validation : `submit` demande la relecture, `approve` / `changes`
 *  la tranchent — et seul le valideur désigné (ou un admin) le peut. */
export function useGuideValidation(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      testId,
      action,
      comment,
    }: {
      testId: string;
      action: 'submit' | 'approve' | 'changes';
      comment?: string;
    }) =>
      api.post<{ guideStatus: GuideStatus }>(
        `/api/tests/${encodeURIComponent(testId)}/validation`,
        { action, comment },
      ),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

export function useUpdateAudit(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: AuditPatch }) =>
      api.put<{ audit: { id: string } }>(`/api/audits/${encodeURIComponent(id)}`, patch),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

/** Ajoute une mesure à une triangulation existante. La note et le pilier
 *  rattaché sont ce qui alimente la lecture de concordance et la notation. */
export function useAddTriangulationRow(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ triangulationId, row }: { triangulationId: string; row: TriangulationRowPatch }) =>
      api.post<{ row: { id: string } }>(
        `/api/triangulations/${encodeURIComponent(triangulationId)}/rows`,
        row,
      ),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

export function useUpdateTriangulationRow(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rowId, patch }: { rowId: string; patch: TriangulationRowPatch }) =>
      api.put<{ row: { id: string } }>(`/api/triangulations/rows/${encodeURIComponent(rowId)}`, patch),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

// ---------------------------------------------------------------------------
// LIVRABLES (studio de restitution)
// ---------------------------------------------------------------------------

export function useDeliverables(projectId: string) {
  return useQuery({
    queryKey: ['deliverables', projectId] as const,
    queryFn: () =>
      api
        .get<{ deliverables: Deliverable[] }>(
          `/api/deliverables?projectId=${encodeURIComponent(projectId)}`,
        )
        .then((b) => b.deliverables ?? []),
    enabled: Boolean(projectId),
    staleTime: 30 * 1000,
  });
}

export function useCreateDeliverable(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (d: { title: string; sections: SectionKey[] }) =>
      api.post<{ deliverable: { id: string } }>('/api/deliverables', { ...d, projectId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deliverables', projectId] }),
  });
}

export function useUpdateDeliverable(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { title?: string; sections?: SectionKey[] } }) =>
      api.put<{ deliverable: { id: string } }>(`/api/deliverables/${encodeURIComponent(id)}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deliverables', projectId] }),
  });
}

export function useDeleteDeliverable(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ deleted: boolean }>(`/api/deliverables/${encodeURIComponent(id)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deliverables', projectId] }),
  });
}

// --- Entretiens ---------------------------------------------------------------

export function useInterviews(projectId: string, studyId?: string) {
  return useQuery({
    queryKey: ['interviews', projectId, studyId ?? null] as const,
    queryFn: () => {
      const q = new URLSearchParams({ projectId });
      if (studyId) q.set('studyId', studyId);
      return api.get<{ interviews: Interview[]; transcriber: boolean; recordings?: boolean }>(`/api/interviews?${q.toString()}`);
    },
    enabled: Boolean(projectId),
    staleTime: 15 * 1000,
  });
}

/** La fiche. Tant qu'une transcription tourne, on la relit toutes les 5 s :
 *  la requête qui la porte peut durer plusieurs minutes, et un autre onglet (ou
 *  un rechargement) doit voir l'état avancer. */
export function useInterview(interviewId: string | null) {
  return useQuery({
    queryKey: ['interview', interviewId] as const,
    queryFn: () =>
      api.get<{
        interview: Interview; transcriber: boolean; recordings?: boolean;
        outputs?: InterviewOutput[]; importer?: { youtube: boolean; generate: boolean };
      }>(`/api/interviews/${encodeURIComponent(interviewId!)}`),
    enabled: Boolean(interviewId),
    staleTime: 10 * 1000,
    refetchInterval: (q) => (q.state.data?.interview.transcript_status === 'running' ? 5000 : false),
  });
}

function invalidateInterviews(qc: QueryClient, projectId: string) {
  void qc.invalidateQueries({ queryKey: ['interviews', projectId] });
  void qc.invalidateQueries({ queryKey: ['interview'] });
}

export function useCreateInterview(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: InterviewPatch) => api.post<{ interview: Interview }>('/api/interviews', patch),
    onSuccess: () => invalidateInterviews(qc, projectId),
  });
}

export function useUpdateInterview(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: InterviewPatch }) =>
      api.put<{ interview: Interview }>(`/api/interviews/${encodeURIComponent(id)}`, patch),
    onSuccess: () => invalidateInterviews(qc, projectId),
  });
}

export function useDeleteInterview(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ deleted: boolean }>(`/api/interviews/${encodeURIComponent(id)}`),
    onSuccess: () => invalidateInterviews(qc, projectId),
  });
}

/** Envoi d'un enregistrement, en trois temps : le serveur ouvre l'envoi, le
 *  navigateur dépose le fichier (vers le bucket directement en production),
 *  le serveur vérifie. `XMLHttpRequest` et non `fetch` : c'est le seul qui
 *  rapporte la progression d'un envoi — indispensable pour 1 Go de vidéo. */
export function useUploadRecording(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file, onProgress }: { id: string; file: File; onProgress: (ratio: number) => void }) => {
      const base = `/api/interviews/${encodeURIComponent(id)}/recording`;
      const { upload } = await api.post<{ upload: { url: string; method: string; headers: Record<string, string> } }>(
        base,
        // `consent` : l'utilisateur a attesté l'accord de la personne enregistrée.
        { name: file.name, type: file.type, size: file.size, consent: true },
      );
      await putFile(upload, file, onProgress);
      return api.post<{ interview: Interview }>(`${base}/complete`);
    },
    onSuccess: () => invalidateInterviews(qc, projectId),
  });
}

export function useTranscribeInterview(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ count: number; interview: Interview }>(`/api/interviews/${encodeURIComponent(id)}/transcribe`),
    // La transcription verse des verbatims dans l'étude : tout ce qui en
    // dépend (fiche d'étude, insights, indicateurs) doit se relire.
    onSettled: () => {
      invalidateInterviews(qc, projectId);
      invalidateProject(qc, projectId);
    },
  });
}

/**
 * Importer un entretien (lien YouTube, fichier texte, texte collé) ou repartir
 * de sa transcription, et générer les synthèses demandées (054).
 */
export function useImportInterview(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: {
      id: string;
      source: 'youtube' | 'fichier' | 'texte' | 'transcription';
      url?: string; text?: string; fileName?: string;
      formats: InterviewOutputFormat[]; tone: InterviewTone;
    }) => api.post<{ generated: InterviewOutputFormat[]; interview: Interview; outputs: InterviewOutput[] }>(
      `/api/interviews/${encodeURIComponent(id)}/import`, body),
    // Les verbatims extraits rejoignent l'étude : tout ce qui en dépend se relit.
    onSettled: () => {
      invalidateInterviews(qc, projectId);
      invalidateProject(qc, projectId);
    },
  });
}

// --- Plan de travail (« Atelier ») -------------------------------------------
//
// Le plan se lit d'un coup (post-it, groupes, verbatims). Les déplacements sont
// appliqués en OPTIMISTE par l'écran (`setQueryData`) : un post-it qu'on lâche
// ne doit pas sauter en attendant le serveur. Chaque écriture invalide ensuite
// le plan, pour revenir à ce que le serveur a vraiment gardé.
import type {
  AssistantMode, Board, BoardClusterProposal, BoardGroup, BoardInsightBody, BoardInsightDraft, BoardNote,
} from './types';

export const boardKey = (projectId: string) => ['board', projectId] as const;

export function useBoard(projectId: string) {
  return useQuery({
    queryKey: boardKey(projectId),
    queryFn: () => api.get<Board>(`/api/projects/${encodeURIComponent(projectId)}/board`),
    staleTime: 15 * 1000,
  });
}

function useBoardMutation<TArgs, TRes>(projectId: string, fn: (args: TArgs) => Promise<TRes>, wide = false) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: boardKey(projectId) });
      // Un insight créé, un persona attribué : le reste du projet change aussi.
      if (wide) invalidateProject(qc, projectId);
    },
  });
}

const boardPath = (projectId: string, rest = '') => `/api/projects/${encodeURIComponent(projectId)}/board${rest}`;

export interface BoardNoteInput { verbatimId?: string; text?: string; x?: number; y?: number }
export interface BoardNotePatch { id: string; x?: number; y?: number; groupId?: string | null; text?: string }

export function useAddBoardNotes(projectId: string) {
  return useBoardMutation(projectId, (body: { items: BoardNoteInput[]; at?: { x: number; y: number }; studyId?: string }) =>
    api.post<{ notes: BoardNote[]; skipped: number }>(boardPath(projectId, '/notes'), body));
}

/** « Traiter dans l'atelier » : dépose tout le terrain d'une étude pas encore sur le plan. */
export function useDepositStudy(projectId: string) {
  return useBoardMutation(projectId, (studyId: string) =>
    api.post<{ notes: BoardNote[]; skipped: number }>(boardPath(projectId, `/studies/${encodeURIComponent(studyId)}`), {}));
}

export function useUpdateBoardNotes(projectId: string) {
  return useBoardMutation(projectId, (notes: BoardNotePatch[]) =>
    api.request<{ updated: number }>(boardPath(projectId, '/notes'), { method: 'PATCH', body: { notes } }));
}

export function useDeleteBoardNote(projectId: string) {
  return useBoardMutation(projectId, (noteId: string) =>
    api.del<{ ok: true }>(boardPath(projectId, `/notes/${encodeURIComponent(noteId)}`)));
}

export function useCreateBoardGroup(projectId: string) {
  return useBoardMutation(projectId, (body: { name: string; noteIds: string[] }) =>
    api.post<{ group: BoardGroup }>(boardPath(projectId, '/groups'), body));
}

export function useUpdateBoardGroup(projectId: string) {
  return useBoardMutation(projectId, ({ id, patch }: { id: string; patch: { name?: string; personaId?: string | null } }) =>
    api.request<{ group: BoardGroup; attributed: number }>(
      boardPath(projectId, `/groups/${encodeURIComponent(id)}`), { method: 'PATCH', body: patch },
    ), true);
}

export function useDeleteBoardGroup(projectId: string) {
  return useBoardMutation(projectId, (groupId: string) =>
    api.del<{ ok: true }>(boardPath(projectId, `/groups/${encodeURIComponent(groupId)}`)));
}

export function useBoardGroupInsight(projectId: string) {
  return useBoardMutation(projectId, ({ groupId, body }: { groupId: string; body: BoardInsightBody }) =>
    api.post<{ insight: { id: string }; group: BoardGroup; evidence: number }>(
      boardPath(projectId, `/groups/${encodeURIComponent(groupId)}/insight`), body,
    ), true);
}

// --- L'assistant de l'atelier : des MUTATIONS (un appel au modèle, à la demande),
// qui n'écrivent rien — le plan n'est donc pas rechargé après elles.

export function useBoardClusters(projectId: string) {
  return useMutation({
    mutationFn: (studyId: string | null) =>
      api.post<{ proposals: BoardClusterProposal[]; considered: number; mode: AssistantMode }>(
        boardPath(projectId, '/assistant/clusters'), studyId ? { studyId } : {}),
  });
}

export function useBoardDraft(projectId: string) {
  return useMutation({
    mutationFn: (groupId: string) =>
      api.post<{ draft: BoardInsightDraft; mode: AssistantMode }>(
        boardPath(projectId, `/assistant/groups/${encodeURIComponent(groupId)}/draft`), {}),
  });
}

export function useBoardAsk(projectId: string) {
  return useMutation({
    mutationFn: ({ question, studyId }: { question: string; studyId: string | null }) =>
      api.post<{ answer: string; noteIds: string[]; mode: AssistantMode }>(
        boardPath(projectId, '/assistant/ask'), { question, ...(studyId ? { studyId } : {}) }),
  });
}
